import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from "./BankingInfoTab.module.css";
import DraftModal from '../../components/modal/DraftModal';

// 1. 유틸리티 함수: 날짜 포맷팅 (컴포넌트 외부 정의로 메모리 절약)
const formatToMySQLDateTime = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";

    const pad = (n, s = 2) => String(n).padStart(s, '0');
    const YYYY = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    const SSS = pad(d.getMilliseconds(), 3);
    
    return `${YYYY}-${MM}-${DD} ${hh}:${mm}:${ss}.${SSS}`;
};

// 2. 내부 컴포넌트: 반복되는 그리드 행 구조화 (가독성 향상)
const InfoRow = ({ label, value }) => (
    <>
        <div className={styles.leftTitle}>{label}</div>
        <div className={styles.bankingInput}>{value}</div>
    </>
);

function BankingInfoTab({ data: propsData }) {
    const { t } = useTranslation();

    const [data, setData] = useState(null);
    const [positionInput, setPositionInput] = useState("");
    const [currentAction, setCurrentAction] = useState(null);
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);

    // props 데이터 동기화
    useEffect(() => {
        if (propsData) {
            setData(propsData);
            setPositionInput(propsData.Position || "");
        }
    }, [propsData]);

    // 모달 열기 핸들러 (useCallback)
    const openDraftModal = useCallback((action) => {
        setCurrentAction(action);
        setIsDraftModalOpen(true);
    }, []);

    // API 요청 액션 설정 (useMemo 사용하여 렌더링 시마다 객체 생성 방지)
    const actionConfigs = useMemo(() => {
        if (!data) return {};
        
        const key = formatToMySQLDateTime(data.time);
        const masterFlag = String(data.Master ?? '0');

        return {
            save: {
                url: '/api/updateBankingStorageDraft',
                body: { 
                    key, 
                    position: positionInput, 
                    draftOpinion: "", // onSubmit에서 주입됨
                    vessel: data.Vessel, 
                    line: data.Line, 
                    box: data.Box, 
                    Master: masterFlag 
                }
            },
            delete: {
                url: '/api/deleteBankingStorageDraft',
                body: { key, Master: masterFlag }
            },
            export: {
                url: '/api/updateBankingStorageDraft',
                body: { 
                    key, 
                    position: "0", 
                    vessel: "", 
                    line: "", 
                    box: "", 
                    Master: masterFlag 
                }
            }
        };
    }, [data, positionInput]);

    // 모달 제출 핸들러 (기존 saveDraft 로직 유지 + 최적화)
    const saveDraft = useCallback(async (opinion) => {
        setIsDraftModalOpen(false);
        
        const config = actionConfigs[currentAction];
        if (!config) return;

        // 의견(opinion) 추가
        const finalBody = { ...config.body, draftOpinion: opinion };

        try {
            const response = await fetch(config.url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(finalBody)
            });
            const result = await response.json();
            alert(result.message || "");
        } catch (err) {
            alert(err.message || t("요청 중 오류가 발생했습니다."));
        }
    }, [actionConfigs, currentAction, t]);

    // 저장 버튼 클릭 핸들러
    const handleSave = useCallback(() => {
        if (!data) return alert(t('항목을 선택해 주세요.'));
        if (data.Position === positionInput) return alert(t('위치가 이전과 같습니다.'));
        
        const n = parseInt(positionInput, 10);
        if (isNaN(n) || n < 1 || n > 100) {
            return alert(t("1에서 100 사이의 숫자를 입력해주세요."));
        }
        openDraftModal('save');
    }, [data, positionInput, openDraftModal, t]);

    return (
        <div className={styles.bankingTabMain}>
            {isDraftModalOpen && (
                <DraftModal
                    onClose={() => setIsDraftModalOpen(false)}
                    onSubmit={saveDraft}
                />
            )}

            <div className={styles.title}>
                <span>{t('Banking Stock Information')}</span>
                <div className={styles.editSaveButtonGroup}>
                    {data && (
                        <>
                            <button onClick={() => openDraftModal('export')}>{t('export')}</button>
                            <button onClick={handleSave}>{t('save')}</button>
                            <button onClick={() => openDraftModal('delete')}>{t('delete')}</button>
                        </>
                    )}
                </div>
            </div>

            {!data ? (
                <div>{t('No data')}</div>
            ) : (
                <div className={styles.gridBox}>
                    <InfoRow label="Organoid Info Key" value={data.Organoid_Info_Key?.replace('hashmark', '#')} />
                    <InfoRow label="Patient Code" value={data.PatientCode} />
                    <InfoRow label="Lab Code" value={data.LabCode} />
                    <InfoRow label="Vessel" value={data.Vessel} />
                    <InfoRow label="Line" value={data.Line} />
                    <InfoRow label="Box" value={data.Box} />

                    <div className={styles.leftTitle}>Position</div>
                    <div className={styles.bankingInput}>
                        <input
                            type="number"
                            value={positionInput}
                            onChange={(e) => setPositionInput(e.target.value)}
                            className={styles.input}
                            min="0" max="100"
                        />
                    </div>

                    <InfoRow label="Passage No." value={data.PassageNo} />
                    <InfoRow label="Freezing Date" value={data.FreezingDate} />
                    <InfoRow label="Keeper" value={data.Keeper} />
                    <InfoRow label="Culture Media" value={data.CultureMedia} />
                    <InfoRow label="Dome/Vial or Cell Number" value={data.domePerVialOrCellNumber} />
                    <InfoRow label="Thawing No." value={data.thawingNo} />
                </div>
            )}
        </div>
    );
}

export default BankingInfoTab;